import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useIssueState} from '@github-ui/issue-pr-state'
import {issuePath} from '@github-ui/paths'
import {Link} from '@primer/react'

import type {SimilarIssueUpdate} from './hooks/use-similar-issues'
import type {SimilarIssue} from './SimilarIssues'
import styles from './SimilarIssues.module.css'
import {useSimilarIssueSubscription} from './subscriptions/similar-issue-subscription'

interface SimilarIssueItemProps {
  issue: SimilarIssue
  repoOwner: string
  repoName: string
  onIssueClick?: (issue: SimilarIssue) => void
  /** Called when an issue is updated via subscription. If not provided, no subscription is created. */
  onIssueUpdate?: (update: SimilarIssueUpdate) => void
}

const IssueIcon = ({state, stateReason}: {state: SimilarIssue['state']; stateReason: SimilarIssue['stateReason']}) => {
  const {sourceIcon} = useIssueState({state, stateReason})
  const Icon = sourceIcon('Issue')
  // eslint-disable-next-line react-hooks/static-components
  return <Icon />
}

/** Inner component that always subscribes - only rendered when we have valid props */
function SimilarIssueSubscriptionInner({
  nodeId,
  onIssueUpdate,
}: {
  nodeId: string
  onIssueUpdate: (update: SimilarIssueUpdate) => void
}) {
  useSimilarIssueSubscription(nodeId, {onUpdate: onIssueUpdate})
  return null
}

/**
 * Wrapper component that subscribes to issue updates.
 * Handles its own guard logic - only subscribes when onIssueUpdate is provided.
 * This pattern ensures useSubscription isn't called with empty/invalid data and keeps
 * the subscription logic self-contained.
 */
function SimilarIssueSubscription({
  nodeId,
  onIssueUpdate,
}: {
  nodeId: string
  onIssueUpdate?: (update: SimilarIssueUpdate) => void
}) {
  if (!onIssueUpdate) {
    return null
  }
  return <SimilarIssueSubscriptionInner nodeId={nodeId} onIssueUpdate={onIssueUpdate} />
}

/**
 * Individual similar issue item with optional subscription support.
 * When onIssueUpdate is provided and the issue has a nodeId, subscribes to issue updates.
 */
export function SimilarIssueItem({issue, repoOwner, repoName, onIssueClick, onIssueUpdate}: SimilarIssueItemProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  return (
    <li className={styles.item}>
      <SimilarIssueSubscription nodeId={issue.nodeId} onIssueUpdate={onIssueUpdate} />
      <IssueIcon state={issue.state} stateReason={issue.stateReason} />
      <Link
        href={issuePath({owner: repoOwner, repo: repoName, issueNumber: issue.number})}
        {...getHovercardAttributesForType('issue', {owner: repoOwner, repo: repoName, issueNumber: issue.number})}
        target={onIssueClick ? undefined : '_blank'}
        onClick={
          onIssueClick
            ? (e: React.MouseEvent<HTMLAnchorElement>) => {
                // Allow cmd/ctrl+click and middle-click to open in new tab
                // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                if (e.button === 1 || e.shiftKey || e.ctrlKey || e.metaKey) return
                e.preventDefault()
                onIssueClick(issue)
              }
            : undefined
        }
      >
        {issue.title} <span className={styles.number}>#{issue.number}</span>
      </Link>
    </li>
  )
}
